import React, { useEffect } from 'react'

import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'

import { indexes } from '@mab-common/utils'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { CtaProps } from '~/components/atoms/Ctas/Ctas'
import type { ImageProps } from '~/components/atoms/Image/Image'
import type { KeyMessageBannerProps } from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import type { PDPValuationCtaProps } from '~/components/atoms/PDPValuationCta/PDPValuationCta'
import type { ArticleHeaderProps } from '~/components/blocks/ArticleHeader/ArticleHeader'
import ArticleContainer from '~/containers/ArticleContainer/ArticleContainer'
import {
  articleCard,
  artworkLimiter,
  artworkOrder,
  ctas,
  expert,
  getImage,
  image,
  internationalizedArrayField,
  internationalizedField,
  internationalizedStringField,
  reportCard,
  videoEmbed,
} from '~/schemas/common'
import { artworkCard, link } from '~/schemas/common'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useSanity } from '~/services/sanity'
import { processInverseQueryResults } from '~/utils/articles/translationHelpers'
import { buildTranslatedUrlsQuery } from '~/utils/getHreflangUrls'
import { metaQuery } from '~/utils/seo'
import {
  resolveUrlLinksWithTranslation,
  slugFromContext,
  toURL,
  urlFields,
  urlQuery,
} from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'
import EsIndexTypes = indexes.EsIndexTypes

import type { ReportCardProps } from '~/components/blocks/ReportCard/ReportCard'
import { supportedLanguages } from '~/data/lang/settings'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { getArtistIndexData } from '~/utils/getArtistIndexData'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { supportedLocales } from '~/utils/langPaths'

export type TArticleVideo = {
  coverImage: {
    _type: 'image'
    asset: {
      _ref: string
      _type: 'reference'
    }
  }
  summary: string
  title: string
  videoUrl: string
}

export interface PageData {
  articleId: string
  stickyOverrideCta?: string
  leadImage: ImageProps
  updated_at?: string
  publishDate?: string
  lastUpdatedAt?: string
  relatedArtists?: Array<{
    _id: string
    name: string
    image: ImageProps
    slug: string
    totalArtworks: number
  }>
  ctas: CtaProps[]
  modules: Array<{
    _type: string
    _key: string
    title?: string
    skipLinkTitle?: string
    omitSkipLink?: boolean
    isSkipLinkChild?: boolean
    moduleId?: string
  }>
  primaryCategory?: {
    _id: string
    name: string
    slug: {
      current: string
    }
  }
  secondaryCategories?: Array<{
    _id: string
    name: string
    slug: {
      current: string
    }
  }>
  pbModules: Array<{
    _type: string
  }>
  relatedArtworks: ArtworkCardProps[] | null
  relatedSellArtworks: ArtworkCardProps[]
  relatedArticles: ArticlePreviewCardProps[]
  banner: KeyMessageBannerProps
  relatedParentArticles: ArticlePreviewCardProps[]
  articleHeader: ArticleHeaderProps
  body?: PortableTextComponentProps['blocks']
  numberedList?: any
  breadcrumb: BreadcrumbProps['list']
  relatedArtist: {
    name: string
    id: string
    image: ImageProps
    slug: string
    type?: string
    expert: PDPValuationCtaProps['expert']
  }
  relatedArtistWorks?: number
  relatedGenre: {
    name: string
    slug: string
    type?: string
  }
  relatedGenreWorks?: number
  preview: boolean
  studioUrl?: string
  initial?: any
  queryParams: any
  inListingBanners: any
  expert: PDPValuationCtaProps['expert']
  defaultExpert: PDPValuationCtaProps['expert']
  query?: string
  data?: any
  hideExpert?: boolean
  displaySideMenu?: boolean
  keySummary?: any
  video?: TArticleVideo
  podcast?: any
  valuationCTAPosition?: number
  relatedCollections?: any
  advert?: {
    modalImage: ImageProps
    title: string
    introduction: string
    image: ImageProps
    landscapeImage: ImageProps
    position: string
    link: string
  }
  additionalAuthors?: ArticleHeaderProps['author'][]
  isPremium?: boolean
  featuredReport?: ReportCardProps
}

function buildRelatedArticlesQuery(
  slug: string | string[],
  limiter = '',
  category = '',
  articleTaxonomy = '',
  mergeWithCurated = true,
  lang = 'en'
) {
  /**
   * Generate the curated articles query fragment
   * For English: simple dereference
   * For other languages: resolve to translations via translation.metadata
   */
  const buildCuratedQuery = () => {
    if (lang === 'en') {
      return `relatedArticles[]-> {
        ${articleCard}
      }`
    }
    return `relatedArticles[]->{
      "article": *[
        _type == "translation.metadata" && references(^._id)
      ][0].translations[_key == $lang].value->{
        ${articleCard}
      }
    }`
  }

  /**
   * Generate the auto-generated articles query fragment
   * Finds additional articles based on filters
   */
  const buildAutoGeneratedQuery = (
    filters: string,
    limitClause = '[0...3]',
    scoreString = ''
  ) => {
    const scorePipe = scoreString ? `| score(${scoreString})` : ''
    const orderPipe = scoreString
      ? '| order(_score desc, coalesce(publishDate, _createdAt) desc)'
      : limitClause.includes('order')
        ? ''
        : '| order(coalesce(publishDate, _createdAt) desc)'

    // Add && separator if we have filters
    const filtersClause = filters ? `${filters} &&` : ''

    if (lang === 'en') {
      return `*[_type == "article" &&
        _id != ^._id &&
        ${filtersClause}
        (!defined(language) || language == ^.language) &&
        !(_id in coalesce(^.relatedArticles[]->_id, [])) ${limiter}]
        ${scorePipe}
        ${orderPipe}
        ${limitClause} {
          ${articleCard}
        }`
    }
    return `*[_type == "article" &&
      _id != ^._id &&
      ${filtersClause}
      !(_id in coalesce(^.relatedArticles[]->_id, [])) &&
      _id in *[_type == "translation.metadata"].translations[_key == $lang].value._ref
      ${limiter}]
      ${scorePipe}
      ${orderPipe}
      ${limitClause} {
        "article": *[_type == "translation.metadata" && references(^._id)][0]
          .translations[_key == $lang].value-> {
            ${articleCard}
          }
      }`
  }

  // Helper function to build simple related articles query
  const buildSimpleQuery = (additionalFilters = '', limit = 8) => {
    const filters = additionalFilters
      ? `parent._ref == ^.parent._ref ${additionalFilters}`
      : 'parent._ref == ^.parent._ref'

    return `"relatedArticles": (
      coalesce(
        ${buildCuratedQuery()},
        []
      ) +
      ${buildAutoGeneratedQuery(filters, '[0...3]')}
    )[0...${limit}]`
  }

  // Helper function for pattern-based queries
  const buildPatternQuery = (pattern: string, limit = 32) => {
    const filters = `slug.current match "**${pattern}**" &&
      primaryCategory->name != "Podcasts" &&
      primaryCategory->name != "Videos"`

    return `"relatedArticles": (
      coalesce(
        ${buildCuratedQuery()},
        []
      ) +
      ${buildAutoGeneratedQuery(filters, `| order(coalesce(publishDate, _createdAt) desc)`)}
    )[0...${limit}]`
  }

  // patterns to match against the slug
  const patternGroups = [
    ['sellers-guide', 'seller-guide'],
    ['buyers-guide', 'buyer-guide'],
    ['investment-guide'],
    ['collecting-'],
    ['record-prices'],
    ['most-famous'],
  ]

  const priorityPatterns = patternGroups.flat()
  const matchingPattern = priorityPatterns.find(pattern =>
    slug.includes(pattern)
  )

  // Handle special cases
  if (!matchingPattern) {
    const isAuctionWatch = slug.includes('auction-watch')
    if (isAuctionWatch) {
      return buildPatternQuery('auction-watch')
    }

    const isMarketEditorReport = slug.includes('market-editor-report')
    if (isMarketEditorReport) {
      return buildPatternQuery('market-editor-report')
    }

    const podcastVideos = ['videos', 'podcasts']
    if (podcastVideos.includes(category)) {
      const filters = `primaryCategory->slug.current match "${category}" &&
        (
          (count(coalesce(^.relatedArtists, [])) > 0 && count(coalesce(relatedArtists, [])[_ref in ^.^.relatedArtists[]._ref]) > 0) ||
          (count(coalesce(^.relatedArtists, [])) == 0 && count(coalesce(relatedArtists, [])) == 0)
        )`

      return `"relatedArticles": (
        coalesce(
          ${buildCuratedQuery()},
          []
        ) +
        ${buildAutoGeneratedQuery(filters, '| order(coalesce(publishDate, _createdAt) desc)')}
      )[0...12]`
    }

    if (articleTaxonomy === '10-facts') {
      return buildSimpleQuery(
        `&& customTaxonomy match "${articleTaxonomy}"`,
        32
      )
    }

    // For specific categories, filter by primaryCategory
    if (category) {
      return buildSimpleQuery(
        `&& primaryCategory->slug.current == "${category.toLowerCase()}"`,
        32
      )
    }

    // Default: Get curated + auto-generated articles with translation support
    return buildSimpleQuery('', 8)
  }

  // Handle pattern-based related articles with scoring
  const matchingGroup = patternGroups.find(group =>
    group.includes(matchingPattern)
  ) || [matchingPattern]

  const excludeFilters = matchingGroup
    .map(pattern => `!(slug.current match "**${pattern}**")`)
    .join(' && ')

  const excludeFilter = `&& (${excludeFilters})`

  const remainingGroups = patternGroups.filter(
    group => !group.includes(matchingPattern)
  )

  const includePatterns = remainingGroups
    .map(group => {
      if (group.length > 1) {
        return `(${group.map(p => `slug.current match "**${p}**"`).join(' || ')})`
      }
      return `slug.current match "**${group[0]}**"`
    })
    .join(' || ')

  const patternFilter = includePatterns ? ` && (${includePatterns})` : ''

  const scoreBoosts = remainingGroups.map((group, index) => {
    const pattern = group[0]
    const boost = remainingGroups.length - index

    if (group.length > 1) {
      const patterns = group
        .map(p => `slug.current match "**${p}**"`)
        .join(' || ')
      return `boost((${patterns}), ${boost})`
    }

    return `boost(slug.current match "**${pattern}**", ${boost})`
  })

  const scoreString = scoreBoosts.join(',')

  // Pattern-based scoring with curated + automated (translation-aware)
  const filters = `parent._ref == ^.parent._ref
    ${excludeFilter} ${patternFilter}`

  if (mergeWithCurated) {
    return `"relatedArticles": (
      coalesce(
        ${buildCuratedQuery()},
        []
      ) +
      ${buildAutoGeneratedQuery(filters, '[0...8]', scoreString)}
    )[0...8]`
  }

  // No curated articles, only auto-generated with scoring
  return `"relatedArticles": ${buildAutoGeneratedQuery(filters, '[0...8]', scoreString)}`
}

export const articleQuery = (
  limiter?: any,
  slug: any = '',
  category: any = '',
  articleTaxonomy: any = '',
  lang: any = 'en',
  locales: readonly string[] = ['en', 'fr']
) => {
  return `*[_type == 'article' && (slug.current == $slug || $slug in _pastSlugs) && (!defined(language) || language == $lang)][0] {
    "articleId": _id,
    language,
    stickyOverrideCta,
    displaySideMenu,
    "_debug_translationMeta": *[_type == "translation.metadata" && references(^._id)][0],
    "keySummary": keySummary[]${resolveUrlLinksWithTranslation(lang)} {
      ...
    },
    "updated_at": _updatedAt,
    lastUpdatedAt,
    publishDate,
    isPremium,
    ${urlQuery},
    ${buildTranslatedUrlsQuery([...locales])},
    ${metaQuery('article')},
    "leadImage": {
      ...leadImage,
      "text": altText,
      "credit": {
        "theme": imageCreditColor,
        "text": imageCredit
      },
      "objectFit": objectFit
    },
    defined('video') => {
      "video": video-> {
        title,
        videoUrl,
        summary,
        coverImage,
      }
    },
    defined('podcast') => {
      "podcast": podcast-> {
        title,
        podcastUrl,
        summary,
        watchTime,
        coverImage,
      }
    },
    ${ctas},
    "advert": {
      "modalImage": advert.modalImage,
      "title": advert.title,
      "introduction": advert.introduction,
      "image": advert.image,
      "landscapeImage": advert.landscapeImage,
      "position": advert.position,
      "link": advert.link
    },
    "relatedArtists": relatedArtists[]-> {
      _id,
      name,
      image,
      "slug": slug.current,
      totalArtworks,
    },
     "primaryCategory": primaryCategory->{
      _id,
      "name": ${internationalizedStringField('name')},
      "slug": slug.current
    },
    "secondaryCategories": secondaryCategories[]-> {
      _id,
      "name": ${internationalizedStringField('name')},
      "slug": slug.current
    },
    "modules": modules[]${resolveUrlLinksWithTranslation(lang)}{
      ...,
      body[]${resolveUrlLinksWithTranslation(lang)},
      ${image},
      skipLinkTitle,
      omitSkipLink,
      isSkipLinkChild,
      moduleId,
      "link": {
        "externalLink": linkExternal,
        "internalLink": link->{
          ${urlQuery},
        },
      },
      _type == "magazineImage" && link->_type == "artwork" && link->isVisible == true && link->_isDeleted != true => {
        "artwork": link->{
          ${urlQuery},
          title
        }
      },
      _type == "accordion" => {
        ...,
        "data": data[] {
          ...,
          "body": body[]${resolveUrlLinksWithTranslation(lang)},
        }
      },
      defined(teamMember) => {
        "teamMember": teamMember-> {
          name,
          "jobTitle": ${internationalizedStringField('jobTitle')},
          image,
          email,
          "slug": slug.current
        }
      },
      defined(artworkArtists) => {
        "artworkArtists": artworkArtists[]-> {
          "id": _id,
          name
        },
      },
      defined(relatedReport) => relatedReport->{
        ${reportCard}
      },
      _type == "artworkImage" => {
        "image": {
          ...artwork->image,
          "text":altText,
          "alt": altText,
          "credit": {
            "theme": imageCreditColor,
            "text": imageCredit
          },
          "objectFit": objectFit,
        },
        "defaultCaption": {
          "artistName": ^.parent->name,
          "artworkDate": artwork->year,
          "artworkTitle": artwork->title,
        },
        "artwork": artwork-> {
          ${urlQuery},
          title
        }
      },
      _type == "printIndexBlock" => {
        "artist": artist->name,
        "artistId": artist->_id,
      }
    },
    valuationCTAPosition,
    "pbModules": pbModules[]${resolveUrlLinksWithTranslation(lang)} {
      ...,
      ${image},
      ${link},
      "body": body[]${resolveUrlLinksWithTranslation(lang)},
      defined(artworkArtists) => {
        "artworkArtists": artworkArtists[]-> {
          "id": _id,
          name
        },
      },
      "items": items[] {
        ...,
        ${link}
      },
      defined(data) => {
        "data": data[]{
          ...,
          "body": body[]${resolveUrlLinksWithTranslation(lang)},
        }
      }
    },
    ${buildRelatedArticlesQuery(slug, limiter, category, articleTaxonomy, true, lang)},
    defined(relatedArtworks) => {
      "relatedArtworks": *[_type == 'artwork' && _id in ^.relatedArtworks[]._ref] | ${artworkOrder}{
        ${artworkCard}
      }
    },
    "relatedSellArtworks": *[_type == 'artwork' && artist._ref == ^.parent._ref && ${artworkLimiter}] | order(coalesce(sellTicketsCount, -1) desc, indexWeighting desc) [0..4] {
        ${artworkCard}
    },
    "relatedCollections": relatedCollections[]->{
      _id,
      "name": title,
      image,
      ${urlQuery},
      "totalArtworks": count(*[_type == 'artwork' && collection._ref == ^._id && ${artworkLimiter}])
    },
    "relatedParentArticles": *[_type == 'article' && parent._ref == ^.parent._ref && (!defined(language) || language == ^.language) ${
      limiter && limiter
    } && _id != ^._id] | order(date desc)[0...3] {
      ${articleCard}
    },
    defined(*[_type == 'artist' && _id == ^.parent._ref][0]) => {
      "relatedArtist": *[_type == 'artist' && _id == ^.parent._ref][0]{
        name,
        "type": _type,
        "id": _id,
        image,
        "slug": slug.current,
        "expert": expert-> {
          ${expert}
        },
      },
      "relatedArtistWorks": count(*[_type == 'artwork' && artist._ref == ^.parent._ref && ${artworkLimiter}]),
    },
    defined(*[_type == 'genre' && _id == ^.parent._ref][0]) => {
      "relatedGenre": *[_type == 'genre' && _id == ^.parent._ref][0]{
        "name": ${internationalizedField('name')},
        "type": _type,
        "slug": slug.current,
      },
      "relatedGenreWorks": count(*[_type == 'artwork' && genre._ref == ^.parent._ref  && ${artworkLimiter}]),
    },
    "banner": *[_type == 'banner' && showOnHomepage != true && references(^._id)] | order(_createdAt desc)[0]{
      title,
      "subtitle": body,
      "images": {
        image
      },
      "textMask": applyOverlay,
      ${link}
    },
    "articleHeader": {
      title,
      subtitle,
      "author": {
        "name": author->name,
        "slug": author->slug.current,
        "image": ${getImage('author')},
        "bio": author->listSummary,
        "bioFallback": ${internationalizedArrayField('author->description', lang)}
      },
      "reviewedBy": {
        "name": reviewedBy->name,
        "slug": reviewedBy->slug.current,
        "image": ${getImage('reviewedBy')}
      },
      "updatedAt": coalesce(lastUpdatedAt, _updatedAt),
      publishDate,
      readtime,
      customTaxonomy,
      overrideAuthor,
      "articleType": parent->_type,
      "articleTypeSlug": coalesce(parent->slug.current,primaryCategory->slug.current),
      "articleTypeTitle": coalesce(${internationalizedStringField('parent->name')}, ${internationalizedStringField('primaryCategory->name')}),
      defined(primaryCategory) => {
        "articleType": "category"
      },
      "headerTableItems": headerTableItems[] {
        ...
      }
    },
    "author": author->{
      name,
      "jobTitle": ${internationalizedStringField('jobTitle')},
      "description": ${internationalizedArrayField('description', lang)},
      "slug": slug.current,
      image,
      linkedIn
    },
    "additionalAuthors": additionalAuthors[]-> {
        name,
        "slug": slug.current,
        image,
        "bio": listSummary,
        "bioFallback": description
    },
    "body": body[]${resolveUrlLinksWithTranslation(lang)},
    "numberedList": numberedList->{
      title,
      "intro": ${internationalizedArrayField('intro', lang)},
      "slides": coalesce(items, slides)[] {
        "artworkName": ${internationalizedStringField('artworkName')},
        "text": coalesce(${internationalizedArrayField('body', lang)}, ${internationalizedArrayField('text', lang)}),
        defined(artwork) => {
          "image": {
            ...artwork->image,
            "text": altText,
            "alt": coalesce(altText, artwork->image.alt),
            "credit": {
              "theme": imageCreditColor,
              "text": imageCredit
            },
            "objectFit": objectFit,
            includeOriginalRes
          },
          "artwork": artwork-> {
            ${urlQuery},
            title
          }
        },
        !defined(artwork) => {
          ${image}
        },
        price,
        "title": ${internationalizedStringField('title')},
        currency,
        originalCurrency,
        ${videoEmbed}
      } | order(price desc) | order(date desc),
    },
    "expert": expert-> {
      ${expert}
    },
    "defaultExpert": *[_id == '0c31acc7-9223-40c8-b0be-fc0308d9c18f'][0]{
      ${expert}
    },
    hideExpert,
    "articleLdSummary": coalesce(meta.description, pt::text(keySummary)),
    "featuredReport": featuredReport->{
      ${reportCard}
    },
  }`
}

// Loaded on demand so the loader, comlink and stega only reach the browser in a
// preview session, never in the bundle regular visitors download.
const ArticlePreview = dynamic(
  () => import('~/components/preview/ArticlePreview')
)

const ArticlePage: NextPage<PageData> = (props: PageData) => {
  const { articleId, preview, query } = props
  const { sendSearchInsightsViewEvent } = useSearchAnalyst()

  useEffect(() => {
    if (!preview && articleId) {
      sendSearchInsightsViewEvent(SearchAnalystMethods.viewedObjectIDs, {
        eventName: SearchAnalystEventNames.articlePageViewed,
        indexType: EsIndexTypes.ARTICLES,
        objectIDs: [articleId],
      }).catch(error => {
        console.error('Failed to send article page view analytics:', error)
      })
    }
  }, [articleId, preview, sendSearchInsightsViewEvent])

  if (!props.articleHeader) {
    return null // or return a loading state/error state
  }

  if (preview && query) {
    return <ArticlePreview pageData={props} />
  }

  return <ArticleContainer {...props} />
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticPropsOriginal(context) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { client, limiter, isPreview } = await useSanity(context)

      const originalSlug = context?.params?.slug as string
      const slug = originalSlug.toLowerCase()
      const lang = (context?.params?.lang as Locale) || 'en'
      const messages = await loadMessages(lang)

      const query = articleQuery(
        limiter,
        slug,
        ((context?.params?.category as string) || '').toLowerCase(),
        ((context?.params?.articleTaxonomy as string) || '').toLowerCase(),
        lang,
        supportedLocales
      )

      // In preview, fetch through the loader so the response carries a source
      // map. `useQuery` treats a seed without one as still loading and never
      // subscribes, so a plain fetch would leave the preview frozen.
      // Stega is off here: the encoded copy is only needed as the live query's
      // seed. Rendering the page from it would triple the props payload, since
      // every string carries invisible metadata.
      // Imported here rather than at the top so the React loader stays out of
      // the page's client bundle.
      const initial = isPreview
        ? await (
            await import('~/services/sanity/previewLoader')
          ).loadQuery<any>(
            query,
            { slug, lang },
            { perspective: 'drafts', stega: false }
          )
        : null

      const data = (
        initial
          ? initial.data
          : await client.fetch<{
              title: string
              modules: PageData['modules']
              pbModules: any
              numberedList: PageData['numberedList']
              skipLinks: any
              meta: any
              articleHeader: any
              relatedArtworks: ArtworkCardProps[] | null
              relatedSellArtworks: ArtworkCardProps[]
              relatedArticles: any[]
            }>(query, {
              slug: slug,
              lang: lang,
            })
      ) as any

      // Handle missing translations - redirect to English version if article exists there
      if (!data && lang !== 'en') {
        // Check if article exists in English
        const englishExists = await client.fetch<{
          _id: string
          url: any
        } | null>(
          `*[_type == 'article' && slug.current == $slug && (!defined(language) || language == 'en')][0]{ _id, "url": ${urlFields} }`,
          { slug }
        )

        if (englishExists) {
          return {
            redirect: {
              destination: toURL(englishExists.url),
              permanent: false,
            },
          }
        }
      }

      // If data is still null, return 404
      if (!data) {
        return {
          notFound: true,
        }
      }

      // Post-process related articles if they have nested structure {article: {...}}
      if (data.relatedArticles?.length > 0 && data.relatedArticles[0].article) {
        data.relatedArticles = processInverseQueryResults(
          data.relatedArticles,
          lang
        )
      }

      // check if original slug is different from lowercase slug
      // if so, redirect to lowercase slug
      if (data && slug !== originalSlug && context?.params) {
        return {
          redirect: {
            destination: `/${context?.params?.category}/${context?.params?.articleTaxonomy}/${slug}`,
            permanent: true,
          },
        }
      }

      if (data?.modules && data.modules.length > 0) {
        await Promise.all(
          data.modules.map(async (item: any) => {
            if (item._type === 'artworkData') {
              try {
                return await getArtistIndexData({
                  artworkData: item,
                  yearsBack: 5,
                })
              } catch (e) {
                console.log(e)
                // prevent falling over
              }
            }
            if (item._type === 'accordion') {
              return {
                ...item,
                variant: 'magazine',
              }
            }
            return item
          })
        )
      }

      if (data?.pbModules && data.pbModules.length > 0) {
        await Promise.all(
          data.pbModules.map(async (item: any) => {
            if (item._type === 'artworkData') {
              try {
                const artworkData = await getArtistIndexData({
                  artworkData: item,
                  yearsBack: 5,
                })
                return artworkData
              } catch (e) {
                console.log(e)
                // prevent falling over
              }
            }
            return item
          })
        )
      }

      // `lang` is bound by articleQuery, so it has to travel with the params
      // for the client-side live query to resolve identically.
      const queryParams = { slug: slugFromContext(context), lang }

      const { params } = context

      const path = Object.values(params as any).join('/')
      const allLanguages = ['en', ...supportedLanguages]

      const fullPath = `/${path}`
      const cleanPath = allLanguages.reduce(
        (path, lang) => path.replace(`/${lang}/`, '/'),
        fullPath
      )

      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        cleanPath.split('/').filter(x => x !== ''),
        '',
        undefined,
        messages,
        lang
      )

      const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

      if (Object.keys(data).length < 1) return { notFound: true }

      if (!data?.meta) {
        data.meta = {}
      }

      if (!data?.meta?.title) {
        data.meta.title = `${data.articleHeader.title} | MyArtbroker`
      }

      const sellArtworks = data?.relatedSellArtworks ?? []
      const otherArtworks = data?.relatedArtworks ?? []

      let relatedArtworks: ArtworkCardProps[] | null =
        sellArtworks.length > 0
          ? [...sellArtworks, ...otherArtworks]
          : [...otherArtworks]

      if (relatedArtworks.length == 0) {
        relatedArtworks = null
      }

      const props = {
        notFound: Object.keys(data).length < 1,
        ...data,
        pageType: 'article',
        relatedArtworks,
        queryParams,
        preview: isPreview,
        // Only sent while previewing: the query, the loader's initial response
        // and the Studio URL for stega edit links.
        ...(isPreview && {
          query,
          // The loader's source map and perspective. `variant` is left out
          // because the loader leaves it undefined and Next refuses to
          // serialize that. The store treats an undefined sourceMap as still
          // loading and never subscribes, so it must be passed as returned.
          //
          // `data` is not sent again: the page already spreads it into the
          // props, and ArticlePreview seeds the live query from those.
          initial: initial
            ? {
                sourceMap: initial.sourceMap,
                perspective: initial.perspective,
              }
            : null,
          studioUrl: process.env.NEXT_PUBLIC_SANITY_STUDIO_URL || '',
        }),
        isMagazine: true,
        breadcrumb,
        breadcrumbData: breadcrumb,
        breadcrumbJsonLd,
        jsonLd: {
          faqJsonLd: data.numberedList
            ? data.numberedList.slides
            : data.modules,
          articleJsonLd: data,
        },
        noAnimatedTheme: true,
      }

      return {
        props: props,
      }
    } catch (error) {
      console.log(
        'Error in articles/category/articleTaxonomy getStaticProps:',
        error
      )

      // 301 redirect to the category page if the page is not found, otherwise default to articles
      let redirect = '/articles'

      if (context?.params?.category) {
        const categoryParam = context.params?.category

        if (
          categoryParam &&
          typeof categoryParam === 'string' &&
          categoryParam.startsWith('artist-')
        ) {
          // Handle artist category
          redirect = `${context.params.lang === 'en' ? `` : `/${context.params.lang}`}/${categoryParam}/articles`
        } else {
          redirect = `${context.params.lang === 'en' ? `` : `/${context.params.lang}`}/articles/${categoryParam}`
        }
      }

      return {
        props: {},
        redirect: {
          permanent: true,
          destination: redirect,
        },
      }
    }
  }

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async (context: any) => {
  const result = await getStaticPropsOriginal(context)

  if ('props' in result) {
    return {
      props: await addTranslations(result.props, context),
    }
  }

  return result
})

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default ArticlePage
